import { SxProps } from "@mui/material";
import { App, View } from "@pimo/pimo-app-builder";
import {
  ArtefactsProgressCardV2,
  DerivePimoTableProps,
  GenericStatusCardV2,
  MultipleBadgeCardV2,
  PimoTable,
  type PimoTableEventNames,
  type PimoTableEventPayload,
  PimoTableFilterEventPayload,
  PimoTableSortEventPayload,
  SortDirection,
  TabLayout,
  TableChipCell,
  TableDateCell,
  TableEditCell,
  TableTextCell,
  TableUpdateStatusCell,
} from "@pimo/pimo-components";
import { ResourceLockPlugin } from "@pimo/resource-lock-plugin";
import {
  EXTENDED_LINES_OF_BUSINESS,
  type Initiative,
  INITIATIVE_PHASES,
  type InitiativeFilterData,
  type InitiativeSortData,
  InitiativeWithImpactSums,
  LINES_OF_BUSINESS,
} from "aint-types";
import {
  EDITING_INITIATIVES_CURRENTLY_LOCKED_TOOLTIP,
  filterInitiatives,
  formatDate,
  formatDateUTC,
  getColorsAndTextForInitiativePhaseChips,
  getMonthAndYear,
  getUpdateChipColorBasedOnUpdateStatus,
  IN_COLORS,
  LOCK_RESOURCE_NAMES,
  OES_WITH_DEPENDENT_FIELDS,
  OES_WITH_EXTENDED_LINE_OF_BUSINESS_OPTIONS,
  sortInitiatives,
} from "aint-utils";
import { generatePath } from "react-router-dom";

import { InitiativePopup } from "../../../components/initative-popup/initative-popup.tsx";
import { InitiativesStatusTableContainer } from "../../../components/initiatives-status-table/initiatives-status-table-container.tsx";
import { ReportTitleCard } from "../../../components/report-title-card/report-title-card.tsx";
import { FORM_FIELD_INFO_TEXT } from "../../../form/form-field-info-text.ts";
import { InAppState } from "../../app.tsx";
import { APP_ROUTES } from "../../constants.ts";
import { theme } from "../../theme.ts";
import { convertNumberToMillions } from "../../utils/value-converters.ts";

export function buildInitiativesView(
  view: View<InAppState, TabLayout>,
  app: App<InAppState>
) {
  const viewTitle = view.addComponent({
    component: ReportTitleCard,
    layoutProps: {
      xs: 12,
      viewname: "initiatives",
    },
  });

  viewTitle.mapState(
    ({
      currentOEInitiatives,
      currentOE,
      initiativesLockDetails,
      userProfile,
    }) => {
      const [currentMonth, currentYear] = getMonthAndYear();
      const hasEditRight =
        userProfile != null &&
        (userProfile.role === "editor" || userProfile.role === "admin");

      const initiativesAvailableForReporting =
        (currentOEInitiatives?.initiatives ?? []).filter(
          ({ draft, initiativePhase, updateStatus }) =>
            !draft &&
            initiativePhase !== "L0 - Idea" &&
            updateStatus !== "ended"
        ) ?? [];
      const allInitiativesAreLocked = !initiativesAvailableForReporting.some(
        ({ id }) => !initiativesLockDetails?.[id]?.isLocked
      );

      return {
        title: `Status Report Impact Navigator - ${
          currentOE?.operatingEntity ?? ""
        } (${(currentMonth + 1).toString().padStart(2, "0")}/${currentYear})`,
        showReportButton:
          hasEditRight && initiativesAvailableForReporting.length > 0,
        reportButtonLocked: allInitiativesAreLocked,
      };
    }
  );

  viewTitle.on("edit-button:click", () => {
    const {
      currentOEInitiatives,
      currentOE,
      initiativesLockDetails,
      userProfile,
    } = app.getAppState();
    const firstUnlockedInitiative = currentOEInitiatives?.initiatives?.filter(
      (init) => {
        const lockDetails = initiativesLockDetails?.[init.id];

        return (
          !init.draft &&
          init.initiativePhase !== "L0 - Idea" &&
          init.updateStatus !== "ended" &&
          (!lockDetails?.isLocked ||
            lockDetails?.lockedBy === userProfile?.email)
        );
      }
    )?.[0];

    if (!currentOE || !firstUnlockedInitiative) {
      return;
    }

    lockReporting(firstUnlockedInitiative.id.toString());

    app.navigate(
      generatePath(APP_ROUTES.impactUpdate, {
        oeId: currentOE.id,
        viewname: "initiatives",
        initiativeUid: firstUnlockedInitiative.uid.toString(),
      })
    );
  });

  const initiativePhasesCard = view.addComponent({
    component: MultipleBadgeCardV2,
    layoutProps: {
      xs: 12,
      sm: 12 / 2,
      md: 12 / 2,
      viewname: "initiatives",
    },
  });

  initiativePhasesCard?.mapState((state) => {
    const initiativePhaseCount =
      state.currentOEInitiatives?.phasesOverview ?? {};

    const badges = INITIATIVE_PHASES.map((phase) => {
      const { chipColor, label, textColor } =
        getColorsAndTextForInitiativePhaseChips(phase);
      const count = initiativePhaseCount[phase] ?? 0;
      return {
        color: chipColor,
        title: label,
        text: `${count}`,
        textColor,
      };
    });

    // Calculate the total
    const total = Object.values(initiativePhaseCount).reduce(
      (acc, count) => acc + count,
      0
    );

    badges.push({
      color: "#d8d8d8",
      title: "Total",
      text: `${total}`,
      textColor: theme.palette.primary.contrastText,
    });

    return {
      cardIcon: "linechart.svg",
      headlineText: "Initiative Phases",
      badges,
    };
  });

  const overallProgressCard = view.addComponent({
    component: ArtefactsProgressCardV2,
    layoutProps: {
      xs: 12,
      sm: 12 / 2,
      md: 12 / 4,
      viewname: "initiatives",
    },
  });

  overallProgressCard.mapState((appState) => {
    const { updateStatusOverview } = appState.currentOEInitiatives;

    return {
      cardIcon: "bars.svg",
      cardTitle: "OE Monthly Reporting Progress",

      progressBarProps: {
        color: IN_COLORS.lightBlue,
        displayValue: `${updateStatusOverview?.updateStatus?.["completed"] ?? 0}/${updateStatusOverview?.numberOfInitiatives} (${updateStatusOverview?.updateProgress ?? 0} %)`,
        value: updateStatusOverview?.updateProgress ?? 0,
        label: "Reporting Completed",
      },
      titleProps: { color: theme.palette.primary.contrastText },
    };
  });

  const updatesCompletedCard = view.addComponent({
    component: GenericStatusCardV2,
    layoutProps: {
      xs: 12,
      sm: 12 / 2,
      md: 12 / 4,
      viewname: "initiatives",
    },
  });

  updatesCompletedCard.mapState((state) => {
    const { updateStatusOverview } = state.currentOEInitiatives;

    return {
      title: "OE Monthly Reporting Status",
      cardIcon: "checklist.svg",
      statuses: [
        {
          status: "green",
          name: "Overdue",
          count: Number(updateStatusOverview?.updateStatus?.["overdue"] ?? 0),
          imageUrl: "mood-sad.svg",
        },
        {
          status: "yellow",
          name: "Due",
          count: Number(updateStatusOverview?.updateStatus?.["pending"] ?? 0),
          imageUrl: "mood-neutral.svg",
        },
        {
          status: "red",
          name: "Completed",
          count: Number(updateStatusOverview?.updateStatus?.["completed"] ?? 0),
          imageUrl: "mood-smile.svg",
        },
      ],
    };
  });

  const statusTableDefinition = [
    { component: TableTextCell },
    { component: TableDateCell },
    { component: TableDateCell },
    { component: TableTextCell },
    { component: TableTextCell },
    { component: TableChipCell },
    { component: TableUpdateStatusCell },
    { component: TableEditCell },
  ] as const;

  const statusTable = new PimoTable(statusTableDefinition);

  const initiativesStatusTable = view.addComponent<
    DerivePimoTableProps<typeof statusTable>,
    PimoTableEventNames,
    PimoTableEventPayload
  >({
    component: statusTable,
    layoutProps: {
      viewname: "initiatives",
      xs: 12,
    },
  });

  initiativesStatusTable.on("sort", (event) => {
    const payload = event?.payload as PimoTableSortEventPayload<
      keyof Initiative
    >;
    const state = app.getAppState();
    let direction: SortDirection = "ASC";

    // Swap direction if key is already being sorted by in ascending order.
    if (
      state.initiativeSortData.key === payload?.sort?.key &&
      state.initiativeSortData.direction === "ASC"
    ) {
      direction = "DESC";
    }

    app.patchAppState({
      initiativeSortData: {
        key: payload?.sort?.key,
        direction,
      },
    });
  });

  initiativesStatusTable.on("filter", (event) => {
    const payload =
      event?.payload as PimoTableFilterEventPayload<InitiativeFilterData>;

    app.patchAppState({
      initiativeFilterData: {
        searchQueryFilter: payload?.filter?.searchQueryFilter ?? "",
        yearFilter: payload?.filter?.yearFilter ?? "",
        phaseFilter: payload?.filter?.phaseFilter ?? [],
        updateStatusFilter: payload?.filter?.updateStatusFilter ?? [],
        lineOfBusinessFilter: payload?.filter?.lineOfBusinessFilter ?? [],
        businessUnitFilter: payload?.filter?.businessUnitFilter ?? [],
        categoryFilter: payload?.filter?.categoryFilter ?? [],
      },
    });
  });

  initiativesStatusTable.mapState(
    ({
      initiativeSortData,
      initiativeFilterData,
      currentOE,
      currentOEInitiatives,
      userProfile,
    }) => {
      const initiatives = filterInitiatives(
        initiativeFilterData,
        currentOEInitiatives?.initiatives || []
      );
      const oeId = currentOE?.id ?? "";
      const dependentFields =
        OES_WITH_DEPENDENT_FIELDS[
          currentOE?.operatingEntity as keyof typeof OES_WITH_DEPENDENT_FIELDS
        ] ?? ([] as string[]);
      const showBusinessUnitFilter = dependentFields.includes("businessUnit");
      const showCategoryFilter = dependentFields.includes("category");
      const showExtendedLobOptions = currentOE
        ? OES_WITH_EXTENDED_LINE_OF_BUSINESS_OPTIONS.includes(
            currentOE?.operatingEntity
          )
        : false;

      return {
        container: InitiativesStatusTableContainer,
        containerProps: {
          title: "Initiatives",
          showAddInitiativeButton:
            userProfile?.role === "editor" || userProfile?.role === "admin",
          addInitiativePath: generatePath(APP_ROUTES.addInitiative, {
            oeId,
            viewname: "initiatives",
          }),
          filterData: initiativeFilterData,
          showCategoryFilter,
          showBusinessUnitFilter,
          currency: currentOE?.currency ?? "EUR",
          lineOfBusinessFilterOptions: showExtendedLobOptions
            ? // Move other to the end of the list
              [
                ...LINES_OF_BUSINESS.slice(0, -1),
                ...EXTENDED_LINES_OF_BUSINESS,
                "Other",
              ]
            : LINES_OF_BUSINESS,
        },
        data: sortInitiatives(initiativeSortData, initiatives).map(
          (initiative) => {
            const {
              monitoringEndDate,
              realizedImpact,
              forecastImpact,
              initiativeName,

              startDate,
              dueDate,
              updateStatus,
              initiativePhase,
            } = initiative;
            const isDraft =
              initiative.draft || initiative.initiativePhase === "L0 - Idea";

            const appState = app.getAppState();
            const lockDetail = appState.initiativesLockDetails?.[initiative.id];

            const isLockedByOtherUser =
              lockDetail?.isLocked &&
              !!lockDetail.lockedBy &&
              !!userProfile?.email &&
              lockDetail.lockedBy !== userProfile.email;

            const columnProps = [
              {
                body: initiativeName,
                bodyProps: {
                  sx: {
                    fontWeight: "400",
                  },
                },
              },
              {
                date: formatDateUTC(startDate, "MM/YYYY") as string,
                icon: "calendar.svg",
                iconSize: "30px",
              },
              {
                date: formatDateUTC(monitoringEndDate, "MM/YYYY") as string,
                icon: "calendar.svg",
                iconSize: "30px",
              },
              {
                body:
                  realizedImpact == null || isDraft
                    ? "n/a"
                    : `${convertNumberToMillions(realizedImpact ?? 0)?.toLocaleString("en-US")} Mn ${initiative.currency ?? "EUR"}`,
                containerProps: {
                  sx: { alignItems: "right", justifyContent: "right" },
                },
                tooltipTitle: FORM_FIELD_INFO_TEXT.actualNetBenefits,
                tooltipShow: true,
              },
              {
                body:
                  forecastImpact == null || isDraft
                    ? "n/a"
                    : `${convertNumberToMillions(forecastImpact ?? 0)?.toLocaleString("en-US")} Mn ${initiative.currency ?? "EUR"}`,
                containerProps: {
                  sx: { alignItems: "right", justifyContent: "right" },
                },
                tooltipTitle: FORM_FIELD_INFO_TEXT.plannedNetBenefits,
                tooltipShow: true,
              },
              {
                body:
                  (initiativePhase &&
                    getColorsAndTextForInitiativePhaseChips(initiativePhase)
                      ?.label) ??
                  "",
                chipProps: {
                  sx: {
                    backgroundColor:
                      initiativePhase &&
                      getColorsAndTextForInitiativePhaseChips(initiativePhase)
                        ?.chipColor,
                    color:
                      initiativePhase &&
                      getColorsAndTextForInitiativePhaseChips(initiativePhase)
                        ?.textColor,
                  },
                },
              },
              {
                body: initiative.draft
                  ? "Draft"
                  : `Due: ${formatDate(dueDate)}`,
                icon: initiative.draft
                  ? ""
                  : getUpdateChipColorBasedOnUpdateStatus(
                      updateStatus ?? "completed"
                    )?.icon,
                chipColor: initiative.draft
                  ? theme.palette.secondary.main
                  : getUpdateChipColorBasedOnUpdateStatus(
                      updateStatus ?? "completed"
                    )?.color,

                chipProps: {
                  sx: {
                    minWidth: initiative.draft ? "50px" : "170px",
                    color: initiative.draft ? theme.palette.primary.main : "",
                  },
                },
              },
              {
                cellSx: { padding: 0, width: "25px" },
                isLocked: isLockedByOtherUser,
                hidden:
                  userProfile?.role === "globalViewer" ||
                  userProfile?.role === "viewer",
                textOnHover: isLockedByOtherUser
                  ? EDITING_INITIATIVES_CURRENTLY_LOCKED_TOOLTIP.replace(
                      "$1",
                      lockDetail.lockedBy ?? "unknown"
                    )
                  : "",
                onClick: () => {
                  const currentOE = app.getAppState().currentOE;

                  if (!currentOE || isLockedByOtherUser) {
                    return;
                  }

                  void lockInitiative(initiative.id.toString());

                  app.navigate(
                    generatePath(APP_ROUTES.editInitiatives, {
                      oeId: String(currentOE.id),
                      viewname: "initiatives",
                      initiativeUid: initiative.uid,
                    })
                  );
                },
              },
            ] as const satisfies DerivePimoTableProps<
              typeof statusTable
            >["data"][number]["columnProps"];

            return {
              columnProps,
              rowProps: {
                cardProps: {
                  sx: { cursor: "pointer" },
                },
                onClick: () => {
                  const currentOE = app.getAppState().currentOE;

                  if (!currentOE) {
                    return;
                  }

                  app.patchAppState({
                    currentInitiativePopup: {
                      content: initiative,
                      showPopup: true,
                    },
                  });
                },
              },
            };
          }
        ),
        tableHeaderEntries: generateTableHeaderEntries({
          entries: [
            {
              fieldName: "initiativeName",
              label: "Initiative",
            },
            {
              fieldName: "startDate",
              label: "Project Start",
            },
            {
              fieldName: "monitoringEndDate",
              label: "Monitoring End",
            },
            {
              fieldName: "realizedImpact",
              label: "Actual Net Benefits",
              sx: {
                justifyContent: "flex-end",
              },
            },
            {
              fieldName: "forecastImpact",
              label: "Planned Net Benefits",
              sx: {
                justifyContent: "flex-end",
              },
            },

            {
              fieldName: "initiativePhase",
              label: "Phase",
            },

            {
              fieldName: "updateStatus",
              label: "Reporting Status",
              sx: {
                justifyContent: "left",
              },
            },
            {
              fieldName: "updateStatus",
              label: "",
              sx: {
                justifyContent: "center",
              },
            },
          ],
          initiativeSortData,
        }),
      };
    }
  );
  const initiativePopup = view.addComponent({
    component: InitiativePopup,
    layoutProps: {
      xs: 12,
      viewname: "initiatives",
    },
  });

  initiativePopup.mapState((state) => {
    const currentOE = state.currentOE;
    const dependentFields =
      OES_WITH_DEPENDENT_FIELDS[
        currentOE?.operatingEntity as keyof typeof OES_WITH_DEPENDENT_FIELDS
      ] ?? ([] as string[]);

    return {
      initiative:
        state.currentInitiativePopup?.content ??
        ({} as InitiativeWithImpactSums),
      open: state.currentInitiativePopup?.showPopup ?? false,
      showInterOeRegionField: dependentFields.includes("interOeRegion"),
      showBusinessUnitField: dependentFields.includes("businessUnit"),
      showCategoryField: dependentFields.includes("category"),
      showOeProgramFields: dependentFields.includes("oeProgram"),
      currency: state.currentInitiativePopup?.content?.currency ?? "EUR", // Default to EUR if not set
    };
  });
  initiativePopup.on("popup:closed", () => {
    app.patchAppState({
      currentInitiativePopup: {
        content: null,
        showPopup: false,
      },
    });
  });
  function generateTableHeaderEntries({
    entries,
    initiativeSortData,
  }: {
    entries: {
      fieldName: keyof InitiativeWithImpactSums;
      label: string;
      sx?: SxProps;
    }[];
    initiativeSortData: InitiativeSortData;
  }) {
    return entries.map(({ fieldName, label, sx }) => ({
      fieldName,
      label,
      sx,
      sort:
        initiativeSortData.key === fieldName
          ? initiativeSortData.direction
          : null,
    }));
  }

  function lockInitiative(initiativeId: string) {
    const resourceLockPlugin =
      app.getPluginByName<ResourceLockPlugin<InAppState>>("ResourceLockPlugin");

    return resourceLockPlugin?.lock(
      `${LOCK_RESOURCE_NAMES.initiative}-${initiativeId}`
    );
  }

  function lockReporting(initiativeId: string) {
    const resourceLockPlugin =
      app.getPluginByName<ResourceLockPlugin<InAppState>>("ResourceLockPlugin");

    return resourceLockPlugin?.lock(
      `${LOCK_RESOURCE_NAMES.initiative}-${initiativeId}`
    );
  }
}
